import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useCredits } from '../hooks/useCredits';

export default function CreditsWidget() {
  const { balance, isLoadingBalance } = useCredits();

  const hasBalance = balance !== undefined && balance !== null && balance.total_credits_balance !== undefined && balance.total_credits_balance !== null;
  const displayValue = (balance?.total_credits_balance ?? 0).toLocaleString();

  // Warning booleans come from the backend (business_logic, against
  // credit_alert_config) — never re-derive thresholds here.
  const isLowCredit = balance?.is_low_credit ?? false;
  const isExpiringSoon = balance?.is_expiring_soon ?? false;
  const isExpired = balance?.is_expired ?? false;
  const isExpiryWarning = isExpiringSoon || isExpired;
  const hasAnyWarning = isLowCredit || isExpiryWarning;

  const expiryLabel = balance?.expiry_date ? format(new Date(balance.expiry_date), 'dd MMM') : null;

  const announcement = [
    isLowCredit ? 'Credits balance is low.' : '',
    isExpired ? 'Subscription has expired.' : isExpiringSoon ? 'Subscription is expiring soon.' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`flex items-center rounded-full pl-4 pr-1.5 py-2 gap-3 flex-shrink-0 border ${
        hasAnyWarning ? 'bg-amber-50 border-amber-100' : 'bg-[#EDF5FF] border-blue-100/80'
      }`}
    >
      {isLoadingBalance && !hasBalance ? (
        <div className="w-14 h-4 bg-blue-200/50 rounded animate-pulse" />
      ) : (
        <div className="flex flex-col items-start gap-1">
          <span className={`text-base font-extrabold leading-none flex items-baseline gap-1.5 whitespace-nowrap ${isLowCredit ? 'text-amber-600' : 'text-[#0B2C5C]'}`}>
            {isLowCredit && (
              <i className="ri-error-warning-fill text-xs" role="img" aria-label="Low balance warning" />
            )}
            {displayValue}
            <span className={`text-[10px] font-bold tracking-wider uppercase ${isLowCredit ? 'text-amber-600' : 'text-slate-400'}`}>
              {isLowCredit ? 'Credits Low' : 'Credits'}
            </span>
          </span>
          {/* Under the balance rather than beside it, so the pill stays narrow. */}
          {typeof balance?.credit_bonus === 'number' && balance.credit_bonus > 0 && (
            <span className="text-[10px] font-bold leading-none text-green-600 whitespace-nowrap">
              +{balance.credit_bonus.toLocaleString()} Bonus
            </span>
          )}
        </div>
      )}

      {expiryLabel && (
        <>
          <span className={`w-px h-4 flex-shrink-0 ${hasAnyWarning ? 'bg-amber-200' : 'bg-blue-200/70'}`} />

          <span className={`text-xs font-semibold leading-none flex items-baseline gap-1.5 whitespace-nowrap ${isExpiryWarning ? 'text-amber-600' : 'text-slate-600'}`}>
            {isExpiryWarning && (
              <i className="ri-error-warning-fill text-xs" role="img" aria-label={isExpired ? 'Subscription expired' : 'Subscription expiring soon'} />
            )}
            {isExpired ? `Expired ${expiryLabel}` : `Expires ${expiryLabel}`}
            <span className={`text-[9px] font-bold tracking-wider uppercase ${isExpiryWarning ? 'text-amber-600' : 'text-slate-400'}`}>
              Subscription
            </span>
          </span>
        </>
      )}

      <Link
        to="/credits"
        className={`text-white text-xs font-bold px-4 py-2 rounded-full transition-all flex items-center gap-1 cursor-pointer whitespace-nowrap ${
          hasAnyWarning ? 'bg-amber-500 hover:bg-amber-600' : 'bg-brand-blue hover:bg-[#06428b]'
        }`}
      >
        + Buy Credits
      </Link>

      {/* Announces warning-state changes to screen readers without altering layout. */}
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
